import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ColorMatching.css';

const colors = [
  { name: 'red', hex: '#FF0000' },
  { name: 'blue', hex: '#0000FF' },
  { name: 'green', hex: '#00FF00' },
  { name: 'yellow', hex: '#FFFF00' },
  { name: 'purple', hex: '#800080' },
  { name: 'orange', hex: '#FFA500' },
  { name: 'pink', hex: '#FFC0CB' },
  { name: 'brown', hex: '#A52A2A' }
];

const ColorMatching = () => {
  const [targetColor, setTargetColor] = useState(null);
  const [options, setOptions] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [feedback, setFeedback] = useState('');

  const generateRound = () => {
    const target = colors[Math.floor(Math.random() * colors.length)];
    const shuffledColors = [...colors].sort(() => Math.random() - 0.5);
    const roundOptions = shuffledColors.slice(0, 4);
    
    // Ensure target color is in options
    if (!roundOptions.find(c => c.name === target.name)) {
      roundOptions[Math.floor(Math.random() * 4)] = target;
    }
    
    setTargetColor(target);
    setOptions(roundOptions.sort(() => Math.random() - 0.5));
  };

  const startGame = () => {
    setGameActive(true);
    setGameOver(false);
    setScore(0);
    setTimeLeft(60);
    setFeedback('');
    generateRound();
  };

  const handleColorClick = (selectedColor) => {
    if (!gameActive) return;
    
    if (selectedColor.name === targetColor.name) {
      setScore(prev => prev + 1);
      setFeedback('✅ Correct!');
      setTimeout(() => {
        generateRound();
        setFeedback('');
      }, 500);
    } else {
      setFeedback(`❌ Wrong! That was ${selectedColor.name}, not ${targetColor.name}`);
      setTimeout(() => {
        setFeedback('');
      }, 1500);
    }
  };

  useEffect(() => {
    let timer;
    if (gameActive && timeLeft > 0) {
      timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && gameActive) {
      setGameActive(false);
      setGameOver(true);
    }
    
    return () => clearTimeout(timer);
  }, [timeLeft, gameActive]);

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🎨 Color Matching Game</h1>
        <div className="game-instructions">
          Click on the color that matches the name shown above!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          Score: {score} | Time: {timeLeft}s
        </div>
        
        {!gameActive && !gameOver && (
          <div className="start-screen">
            <p>Ready to test your color knowledge?</p>
            <button className="btn btn-success" onClick={startGame}>
              Start Game
            </button>
          </div>
        )}

        {gameActive && (
          <div className="color-game-area">
            <div className="target-color-name">
              Find: <span style={{ color: targetColor?.hex }}>{targetColor?.name}</span>
            </div>
            
            <div className="game-play-area">
              <div className="color-options">
                {options.map((color, index) => (
                  <div
                    key={index}
                    className="color-option"
                    style={{ backgroundColor: color.hex }}
                    onClick={() => handleColorClick(color)}
                    title={color.name}
                  ></div>
                ))}
              </div>
            </div>
            
            {feedback && (
              <div className={`feedback ${feedback.includes('✅') ? 'correct' : 'incorrect'}`}>
                {feedback}
              </div>
            )}
          </div>
        )}

        {gameOver && (
          <div className="game-over">
            <h2>🎉 Time's Up!</h2>
            <div className="final-score">
              <p>Final Score: {score} colors matched!</p>
              <p>
                {score >= 40 ? '🏆 Color Master!' : 
                 score >= 30 ? '👍 Great job!' : 
                 score >= 20 ? '👌 Not bad!' : '💪 Keep practicing!'}
              </p>
            </div>
          </div>
        )}

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver ? 'Play Again' : 'New Game'}
          </button>
          <Link to="/" className="btn btn-secondary">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ColorMatching;